import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import CustomPagination from "../../shared/CustomPagination/CustomPagination";
import DataTable from "../../shared/dashboards/DataTable/DataTable";
import RebetLoader from "../../CommonComponents/RebetLoader/RebetLoader";
import {
  formatCurrencyInput,
  formatPositiveDecimalInput,
} from "../../CommonComponents/FormattedInput/FormattedInput";
import EditableLimitCell from "./EditableLimitCell";
import { SGP_RISK_COLUMNS, SGP_RISK_SERVER_SORTER, SGP_RISK_SORT_FIELDS } from "./sgpRiskTableData";
import { buildSgpRiskBetsListPath } from "./sgpRiskNavigation";
import styles from "./SGPRisk.module.css";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const SUCCESS_FLASH_DURATION_MS = 1200;
const API_TO_TABLE_SORT_FIELDS = Object.fromEntries(
  Object.entries(SGP_RISK_SORT_FIELDS).map(([tableKey, apiField]) => [
    apiField,
    tableKey,
  ]),
);

function SgpIdCell({ value, record, betsListDateRange }) {
  return (
    <Link
      to={buildSgpRiskBetsListPath(value, {
        sportId: record.sportId,
        leagueId: record.leagueId,
        sportName: record.sport,
        leagueName: record.league,
        startDate: betsListDateRange?.startDate,
        endDate: betsListDateRange?.endDate,
      })}
      className={styles["sgp-risk-table-sgp-id"]}
      title={value}
    >
      {value}
    </Link>
  );
}

function PlCell({ value }) {
  const className = value.positive
    ? styles["sgp-risk-table-pl--positive"]
    : styles["sgp-risk-table-pl--negative"];
  return <span className={className}>{value.text}</span>;
}

function ExpLimitCell({ percentage }) {
  const safePercent = Math.max(0, Math.min(100, percentage));
  return (
    <div
      className={styles["sgp-risk-table-progress"]}
      role="progressbar"
      aria-valuenow={safePercent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className={styles["sgp-risk-table-progress-track"]}>
        <div
          className={styles["sgp-risk-table-progress-fill"]}
          style={{ width: `${safePercent}%` }}
        />
      </div>
      <span className={styles["sgp-risk-table-progress-label"]}>{safePercent}%</span>
    </div>
  );
}

const ADJUSTMENT_PLACEHOLDER = "-";

function CenteredDash() {
  return (
    <span
      className={styles["sgp-risk-table-cell-dash"]}
      data-testid="sgp-risk-unavailable-cell-dash"
    >
      {ADJUSTMENT_PLACEHOLDER}
    </span>
  );
}

function renderCell(
  columnKey,
  record,
  onExposureControlSave,
  onExposureControlClear,
  onSgpAdjustmentClear,
  betsListDateRange,
  currencySymbol,
) {
  switch (columnKey) {
    case "sgpId":
      return (
        <SgpIdCell
          value={record.sgpId}
          record={record}
          betsListDateRange={betsListDateRange}
        />
      );
    case "pl":
      return <PlCell value={record.pl} />;
    case "expLimitPct":
      if (!record.exposureControlsEditable) {
        return <CenteredDash />;
      }
      return <ExpLimitCell percentage={record.expLimitPct} />;
    case "pendingExpLimit":
      if (!record.exposureControlsEditable) {
        return <CenteredDash />;
      }
      return (
        <EditableLimitCell
          initialValue={record.pendingExpLimit}
          ariaLabel="Pending exposure limit"
          formatter={formatCurrencyInput}
          currencySymbol={currencySymbol}
          showClearButton={record.exposureLimitSource === "override"}
          onCommit={
            onExposureControlSave
              ? (pendingExpLimit) =>
                  onExposureControlSave(record, { pendingExpLimit })
              : undefined
          }
          onClear={
            onExposureControlClear
              ? () => onExposureControlClear(record)
              : undefined
          }
        />
      );
    case "sgpAdjustment":
      if (!record.exposureControlsEditable) {
        return <CenteredDash />;
      }
      return (
        <EditableLimitCell
          initialValue={record.sgpAdjustment}
          ariaLabel="SGP adjustment"
          formatter={formatPositiveDecimalInput}
          placeholder={ADJUSTMENT_PLACEHOLDER}
          showClearButton
          onCommit={
            onExposureControlSave
              ? (sgpAdjustment) =>
                  onExposureControlSave(record, { sgpAdjustment })
              : undefined
          }
          onClear={
            onSgpAdjustmentClear
              ? () => onSgpAdjustmentClear(record)
              : undefined
          }
        />
      );
    default:
      return null;
  }
}

const CUSTOM_CELL_KEYS = new Set([
  "sgpId",
  "pl",
  "expLimitPct",
  "pendingExpLimit",
  "sgpAdjustment",
]);

function SGPRiskTable({
  columns = SGP_RISK_COLUMNS,
  rows = [],
  totalRows,
  betsListDateRange,
  currentPage: controlledCurrentPage,
  pageSize: controlledPageSize,
  onPageChange,
  onPageSizeChange,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  itemLabel = "SGPs",
  onExposureControlSave,
  onExposureControlClear,
  onSgpAdjustmentClear,
  sortBy = "exposure_used_percentage",
  sortOrder = "desc",
  onSortChange,
  loading = false,
  currencySymbol,
}) {
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(defaultPageSize);
  const [successFlashState, setSuccessFlashState] = useState({
    rowId: null,
    phase: "a",
  });
  const successFlashTimeoutRef = useRef(null);
  const isServerPaginated = typeof totalRows === "number";
  const currentPage = controlledCurrentPage ?? internalCurrentPage;
  const pageSize = controlledPageSize ?? internalPageSize;

  useEffect(
    () => () => {
      if (successFlashTimeoutRef.current) {
        window.clearTimeout(successFlashTimeoutRef.current);
      }
    },
    [],
  );

  const triggerSuccessFlash = useCallback((rowId) => {
    if (successFlashTimeoutRef.current) {
      window.clearTimeout(successFlashTimeoutRef.current);
    }

    setSuccessFlashState((previous) => ({
      rowId,
      phase: previous.phase === "a" ? "b" : "a",
    }));

    successFlashTimeoutRef.current = window.setTimeout(() => {
      setSuccessFlashState((previous) => ({
        rowId: previous.rowId === rowId ? null : previous.rowId,
        phase: previous.phase,
      }));
    }, SUCCESS_FLASH_DURATION_MS);
  }, []);

  const handleExposureControlSave = useCallback(
    async (record, updates) => {
      await onExposureControlSave?.(record, updates);
      triggerSuccessFlash(record.id);
    },
    [onExposureControlSave, triggerSuccessFlash],
  );

  const paginatedRows = useMemo(() => {
    if (isServerPaginated) {
      return rows;
    }
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage, pageSize, isServerPaginated]);

  const handleExposureControlClear = useCallback(
    async (record) => {
      await onExposureControlClear?.(record);
      triggerSuccessFlash(record.id);
    },
    [onExposureControlClear, triggerSuccessFlash],
  );

  const handleSgpAdjustmentClear = useCallback(
    async (record) => {
      await onSgpAdjustmentClear?.(record);
      triggerSuccessFlash(record.id);
    },
    [onSgpAdjustmentClear, triggerSuccessFlash],
  );

  const builtColumns = useMemo(
    () =>
      columns.map((column) => {
        const sortField = SGP_RISK_SORT_FIELDS[column.key];
        const activeSortKey = API_TO_TABLE_SORT_FIELDS[sortBy];
        const sortedColumn = sortField
          ? {
              ...column,
              sorter: SGP_RISK_SERVER_SORTER,
              sortOrder:
                activeSortKey === column.key
                  ? sortOrder === "asc"
                    ? "ascend"
                    : "descend"
                  : null,
            }
          : column;
        if (!CUSTOM_CELL_KEYS.has(column.key)) {
          return sortedColumn;
        }
        return {
          ...sortedColumn,
          render: (_value, record) =>
            renderCell(
              column.key,
              record,
              onExposureControlSave ? handleExposureControlSave : undefined,
              onExposureControlClear ? handleExposureControlClear : undefined,
              onSgpAdjustmentClear ? handleSgpAdjustmentClear : undefined,
              betsListDateRange,
              currencySymbol,
            ),
        };
      }),
    [
      betsListDateRange,
      columns,
      currencySymbol,
      handleExposureControlClear,
      handleExposureControlSave,
      handleSgpAdjustmentClear,
      onExposureControlClear,
      onExposureControlSave,
      onSgpAdjustmentClear,
      sortBy,
      sortOrder,
    ],
  );

  const handleTableChange = (_pagination, _filters, sorter) => {
    if (!onSortChange) return;
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const sortKey = activeSorter?.columnKey ?? activeSorter?.field;
    const nextSortBy = SGP_RISK_SORT_FIELDS[sortKey];

    if (!nextSortBy || !activeSorter?.order) {
      if (sortKey && SGP_RISK_SORT_FIELDS[sortKey] && !activeSorter?.order) {
        onSortChange({ sortBy: "exposure_used_percentage", sortOrder: "desc" });
      }
      return;
    }

    const nextSortOrder = activeSorter.order === "ascend" ? "asc" : "desc";
    if (nextSortBy === sortBy && nextSortOrder === sortOrder) {
      return;
    }

    onSortChange({
      sortBy: nextSortBy,
      sortOrder: nextSortOrder,
    });
  };

  const handlePageSizeChange = (nextSize) => {
    if (onPageSizeChange) {
      onPageSizeChange(nextSize);
      return;
    }
    setInternalPageSize(nextSize);
    setInternalCurrentPage(1);
  };

  const handlePageChange = (nextPage) => {
    if (onPageChange) {
      onPageChange(nextPage);
      return;
    }
    setInternalCurrentPage(nextPage);
  };

  return (
    <div className={styles["sgp-risk-table"]} data-testid="sgp-risk-table">
      <div className={styles["sgp-risk-table-body"]}>
        {loading ? (
          <div
            className={styles["sgp-risk-table-loader"]}
            data-testid="sgp-risk-table-loader"
          >
            <RebetLoader height="100px" width="100px" />
          </div>
        ) : (
          <DataTable
            id="sgp-risk-table"
            rowKey="id"
            columns={builtColumns}
            dataSource={paginatedRows}
            pagination={false}
            onChange={handleTableChange}
            rowClassName={(record) => {
              const isFlashing = successFlashState.rowId === record.id;
              return [
                "sgp-risk-row",
                isFlashing ? "sgp-risk-row--success-flash" : null,
                isFlashing
                  ? `sgp-risk-row--success-flash-${successFlashState.phase}`
                  : null,
              ]
                .filter(Boolean)
                .join(" ");
            }}
            onRow={(record) => ({
              "data-testid": `sgp-risk-row-${record.id}`,
            })}
          />
        )}
      </div>
      {!loading && (
        <div className={styles["sgp-risk-table-pagination"]}>
          <CustomPagination
            total={totalRows ?? rows.length}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageSizeChange={handlePageSizeChange}
            onPageChange={handlePageChange}
            itemLabel={itemLabel}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
          />
        </div>
      )}
    </div>
  );
}

export default SGPRiskTable;
